import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ShoppingBag } from "lucide-react";
import { SITE, SERVICES } from "@/lib/site";
import { useCart } from "@/lib/cart";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const { itemCount } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setServicesOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-black/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img src="/img/logo-512.png" alt="Toronto Custom Signs & Wraps logo" className="w-10 h-10 object-contain group-hover:scale-105 transition-transform" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-[15px] tracking-tight text-white">Toronto Custom</span>
            <span className="text-[10px] uppercase tracking-[0.28em] text-gold">Signs &amp; Wraps</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9 text-[13px] uppercase tracking-[0.18em] text-white/70 font-medium">
          <NavItem to="/" label="Home" />
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <NavLink to="/services" className={({isActive}) => `transition-colors ${isActive ? "text-gold" : "hover:text-gold"}`} data-testid="nav-services">
              Services
            </NavLink>
            {servicesOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                <div className="w-[280px] bg-ink-900 border border-white/10 shadow-2xl py-3" data-testid="services-dropdown">
                  {SERVICES.map((s) => (
                    <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`nav-service-${s.slug}`} className="block px-5 py-3 text-[12px] tracking-[0.18em] text-white/70 hover:text-gold hover:bg-white/5 transition-colors">
                      {s.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <NavItem to="/gallery" label="Gallery" />
          <NavItem to="/shop" label="Shop" />
          <NavItem to="/blog" label="Blog" />
          <NavItem to="/about" label="About" />
          <NavItem to="/contact" label="Contact" />
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/cart"
            data-testid="header-cart-btn"
            aria-label={`Cart (${itemCount} items)`}
            className="relative text-white/70 hover:text-gold p-2 transition-colors"
          >
            <ShoppingBag size={18} />
            {itemCount > 0 && (
              <span
                data-testid="cart-badge"
                className="absolute -top-1 -right-1 bg-gold text-black text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center"
              >{itemCount > 99 ? "99+" : itemCount}</span>
            )}
          </Link>
          <a href={SITE.phoneHref} data-testid="header-phone" className="hidden md:inline-flex items-center gap-2 text-white/70 hover:text-gold text-[12px] uppercase tracking-[0.18em]">
            <Phone size={14}/> {SITE.phone}
          </a>
          <Link to="/contact" data-testid="header-quote-btn" className="hidden md:inline-flex btn-gold !py-3 !px-5 !text-[11px]">Get a Quote</Link>
          <button data-testid="mobile-menu-toggle" aria-label="Menu" className="lg:hidden text-white p-2" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-black border-t border-white/10 shadow-2xl" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-1">
            <MobileLink to="/" label="Home" />
            <MobileLink to="/services" label="All Services" />
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="px-4 py-2 text-[12px] tracking-[0.18em] uppercase text-white/50 hover:text-gold">— {s.title}</Link>
            ))}
            <MobileLink to="/gallery" label="Gallery" />
            <MobileLink to="/shop" label="Shop" />
            <MobileLink to="/blog" label="Blog" />
            <MobileLink to="/about" label="About" />
            <MobileLink to="/contact" label="Contact" />
            <Link to="/contact" data-testid="mobile-quote-btn" className="btn-gold mt-4 justify-center">Get a Quote</Link>
          </div>
        </div>
      )}
    </header>
  );
}

const NavItem = ({to, label}) => (
  <NavLink to={to} end={to==="/"} data-testid={`nav-${label.toLowerCase()}`} className={({isActive}) => `transition-colors ${isActive ? "text-gold" : "hover:text-gold"}`}>
    {label}
  </NavLink>
);

const MobileLink = ({to, label}) => (
  <NavLink to={to} end={to==="/"} data-testid={`mobile-nav-${label.toLowerCase().replace(/\s/g,'-')}`} className={({isActive}) => `px-2 py-3 text-[13px] tracking-[0.2em] uppercase border-b border-white/5 ${isActive ? "text-gold" : "text-white/80"}`}>
    {label}
  </NavLink>
);
